import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { DropdownComponent, TooltipOptions } from 'marketpro-design-lib';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../../auth/services/auth.service';
import {
  DataFilters,
  DataFiltersService,
} from '../../../services/data-filters.service';
import { SystemGroupsService } from '../../../services/system-groups.service';
import { AuthorizedResourcesService } from '../../../../auth/services/authorized-resources.service';
import { DashboardStatisticsService } from '../../../services/dashboard-statistics.service';
import { AgeGroup, allAgeGroupsLabel } from '../../../models/age-group.model';
import { AgeGroupSelectorComponent } from '../age-group-selector/age-group-selector.component';
import { ToggleableNameValuePair } from '../../../models/name-value-pair.model';
import { TooltipService } from '../../../services/tooltip.service';

@Component({
  selector: 'mpro-age-generations-selector',
  templateUrl: './age-generations-selector.component.html',
  styleUrls: [
    './../dropdown-common.scss',
    './age-generations-selector.component.scss',
  ],
})
export class AgeGenerationsSelectorComponent implements OnInit, OnDestroy {
  public defaultValue = 'All ages & Generations';
  @Input() public loadRangeCustomFilters: () => void;
  @Input() public multiSelect: boolean = false;
  @Input() public selectedGenerations: string[] = [];
  @Input() public selectedAgeGroups: AgeGroup[] = [];
  @Input() public hideGenerations: boolean = false;
  public inputValue: string = this.defaultValue;
  @ViewChild('generations') generations: DropdownComponent;
  @ViewChild(DropdownComponent) public dropdown: DropdownComponent;
  public dataFilters;
  @ViewChild('tooltip')
  public tooltip: ElementRef;
  public tooltipOptions: TooltipOptions = {
    type: 'data-explorer',
    interactiveText:
      'You can’t select Generation while having age groups selected',
  };

  private ngUnsubscribe: Subject<void> = new Subject();

  public getNameValuePair = DataFilters.getNameValuePair;

  public ageGroups = new BehaviorSubject<AgeGroup[] | undefined>(undefined);
  public generationGroups = new BehaviorSubject<
    ToggleableNameValuePair[] | undefined
  >(undefined);
  private selectedGenerationGroups: string[] = [];

  @Output() public valueChanged: EventEmitter<AgeGroup | string> =
    new EventEmitter();
  @Output() public multiItemValueChanged: EventEmitter<(AgeGroup | string)[]> =
    new EventEmitter();
  constructor(
    public authResources: AuthorizedResourcesService,
    public systemGroupsService: SystemGroupsService,
    public authService: AuthService,
    public dashboardService: DashboardStatisticsService,
    private dataFiltersService: DataFiltersService,
    private tooltipService: TooltipService
  ) {}

  public ngOnInit() {
    this.selectedGenerationGroups = this.selectedGenerations;
    this.inputValue =
      this.selectedGenerations.join(', ') ||
      this.selectedAgeGroups
        .map((ageGroup) => ageGroup.displayValue())
        .join(', ') ||
      'All ages & Generations';
    this.dataFiltersService
      .getDataFilters()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((filters) => {
        this.inputValue =
          filters.generation ||
          (filters.ageGroup.displayValue() !== allAgeGroupsLabel &&
            filters.ageGroup.displayValue()) ||
          'All ages & Generations';
        this.dataFilters = filters;
      });
  }

  public ngAfterViewInit() {
    if (this.tooltip) {
      this.tooltipService.setTooltipElement(
        'AgeGenerationsSelector',
        this.tooltip
      );
    }
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public onDropdownOpened() {}

  public toggleDropdowns(
    dropdowns: (DropdownComponent | AgeGroupSelectorComponent)[]
  ) {
    //Open first dropdown from array
    dropdowns.forEach((dropdown, index) => {
      if (dropdown instanceof AgeGroupSelectorComponent) {
        dropdown.dropdown.toggleDropdownWithParam(!index);
      } else {
        dropdown.toggleDropdownWithParam(!index);
      }
    });
  }

  public hideDropdowns(
    dropdowns: (DropdownComponent | AgeGroupSelectorComponent)[]
  ) {
    dropdowns.forEach((dropdown) => {
      if (dropdown instanceof AgeGroupSelectorComponent) {
        dropdown.dropdown.dropdownOpen = false;
      } else {
        dropdown.dropdownOpen = false;
      }
    });
  }

  public generationsChanged(generation: string) {
    this.tooltipService.hideTooltip('AgeGenerationsSelector');
    if (this.multiSelect) {
      if (this.inputValue.includes(generation)) {
        this.selectedGenerationGroups = this.selectedGenerationGroups.filter(
          (genGroup) => genGroup !== generation
        );
      } else {
        this.selectedGenerationGroups.push(generation);
      }
      this.selectedAgeGroups = [];
      this.inputValue = this.selectedGenerationGroups.length
        ? this.selectedGenerationGroups.join(', ')
        : this.defaultValue;
      this.multiItemValueChanged.emit(this.selectedGenerationGroups);
      return;
    }
    this.inputValue = generation;
    this.dataFiltersService.patchDataFilters({
      ageGroup: new AgeGroup(),
      generation,
    });
    this.valueChanged.emit(generation);
  }

  public ageGroupChanged(ageGroup: AgeGroup | AgeGroup[]) {
    this.tooltipService.hideTooltip('AgeGenerationsSelector');
    if (Array.isArray(ageGroup)) {
      this.selectedGenerationGroups = [];
      this.inputValue = ageGroup.length
        ? ageGroup.map((group) => group.displayValue()).join(', ')
        : this.defaultValue;
      this.multiItemValueChanged.emit(ageGroup);
      return;
    }
    this.inputValue = ageGroup.displayValue() || '';
    this.dataFiltersService.patchDataFilters({
      generation: undefined,
      ageGroup,
    });
    this.valueChanged.emit(ageGroup);
  }

  public isGenerationsDisabled(): boolean {
    return this.selectedAgeGroups.length > 0;
  }

  public isGenerationSelected(group: ToggleableNameValuePair) {
    if (this.multiSelect) {
      return this.selectedGenerationGroups.some(
        (inputValue) => group.name === inputValue
      );
    }

    return group.name === this.inputValue;
  }

  public reset() {
    this.inputValue = 'All ages & generation';
    this.dataFiltersService.patchDataFilters({
      generation: undefined,
      ageGroup: new AgeGroup(),
    });
  }

  public onMouseEnter(ev: MouseEvent) {
    if (this.isGenerationsDisabled()) {
      this.tooltipService.showTooltip(ev, 'AgeGenerationsSelector');
    }
  }

  public onMouseLeave() {
    this.tooltipService.hideTooltip('AgeGenerationsSelector');
  }
}
